import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { Flex } from '@lightspeed/flame/Core';
import { Text } from '@lightspeed/flame/Text';
import { themeGet } from '@styled-system/theme-get';

import { Decimal } from '~/main/utils/Decimal';
import { useMoney } from '~/renderer/hooks';
import { getSale, getTip } from '~/renderer/store/receipt/selectors';
import { AppState } from '~/renderer/store/types';

import type { Sale, Tip } from '~/renderer/store/receipt/types';

const TotalAmountWrapper = styled(Flex)`
  flex-direction: column;
  align-items: center;
  padding: ${themeGet('space.4')};
  border-bottom: solid 1px;
  background-color: ${themeGet('colors.black')};
  border-color: ${themeGet('colors.gray-600')};
  color: ${themeGet('colors.white')};
  z-index: 1;
  @media only screen and (max-height: ${themeGet('breakpoints.0')}) {
    padding: ${themeGet('space.1')};
  }
`;

interface TipWrapperProps extends React.HTMLAttributes<HTMLParagraphElement> {
  visible?: boolean;
}

const TipWrapper = styled(Text)<TipWrapperProps>`
  visibility: ${({ visible = true }) => (visible ? 'visible' : 'hidden')};
`;

export const TotalAmountBanner: React.FC = () => {
  const { money } = useMoney();
  const { t } = useTranslation();

  const tip = useSelector<AppState, Tip | null>(getTip);
  const sale = useSelector<AppState, Sale | null>(getSale);
  const enteredTip = new Decimal(tip?.enteredTip?.tipAmount || 0);
  const totalAmountWithTip = tip?.enteredTip?.totalAmountWithTip || sale?.calcTotal;
  return (
    <TotalAmountWrapper>
      <Text aria-label="total amount with tip" fontSize="xxlarge" fontWeight="bold">
        {t('receipt.total')}: {money(totalAmountWithTip || 0)}
      </Text>
      <TipWrapper
        aria-label="tip amount and payment total"
        color="gray-400"
        fontSize="large"
        visible={enteredTip.gt(0)}
      >
        {`${money(tip?.paymentTotal)} + ${t('receipt.tipping.tip', {
          tipAmount: money(enteredTip.toString()),
        })}`}
      </TipWrapper>
    </TotalAmountWrapper>
  );
};
